/**
 * FeeApplyDateModal Component
 * 수수료 저장 후 적용 예정일을 알려주는 모달
 */
import React from 'react';

interface FeeApplyDateModalProps {
  isOpen: boolean;
  applyStartDate: string; // yyyy-MM-dd 형식
  isImmediateApply: boolean;
  onClose: () => void;
}

// 스타일 상수
const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(2px)'
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '420px',
    overflow: 'hidden',
    boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.25)',
    animation: 'feeModalSlideIn 0.2s ease-out'
  },
  header: {
    padding: '20px 24px 16px',
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: '#f0fdf4'
  },
  title: {
    margin: 0,
    fontSize: '1.125rem',
    fontWeight: 600,
    color: '#166534',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  successIcon: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: '#dcfce7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#16a34a',
    fontSize: '0.875rem'
  },
  body: {
    padding: '24px',
    textAlign: 'center' as const
  },
  dateBox: {
    backgroundColor: '#f8fafc',
    border: '2px solid #04B374',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '16px'
  },
  dateLabel: {
    fontSize: '0.875rem',
    color: '#6b7280',
    marginBottom: '8px'
  },
  dateValue: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#04B374'
  },
  dayOfWeek: {
    fontSize: '1rem',
    color: '#16a34a',
    marginLeft: '4px'
  },
  message: {
    fontSize: '0.9375rem',
    color: '#4b5563',
    lineHeight: 1.6
  },
  immediateTag: {
    display: 'inline-block',
    backgroundColor: '#dbeafe',
    color: '#1d4ed8',
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '0.75rem',
    fontWeight: 600,
    marginTop: '12px'
  },
  nextWeekTag: {
    display: 'inline-block',
    backgroundColor: '#fef3c7',
    color: '#d97706',
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '0.75rem',
    fontWeight: 600,
    marginTop: '12px'
  },
  footer: {
    padding: '16px 24px',
    backgroundColor: '#f9fafb',
    display: 'flex',
    justifyContent: 'center'
  },
  button: {
    padding: '12px 32px',
    borderRadius: '8px',
    fontWeight: 600,
    fontSize: '0.9375rem',
    cursor: 'pointer',
    backgroundColor: '#04B374',
    border: 'none',
    color: '#ffffff',
    transition: 'all 0.2s'
  }
};

/**
 * 날짜 문자열을 한국어 요일 포함 형식으로 변환
 * @param dateStr yyyy-MM-dd 형식
 */
const formatDateWithDayOfWeek = (dateStr: string): { date: string; dayOfWeek: string } => {
  const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
  const date = new Date(dateStr);
  const dayOfWeek = dayNames[date.getDay()];

  // yyyy-MM-dd -> yyyy년 MM월 dd일
  const parts = dateStr.split('-');
  const formattedDate = `${parts[0]}년 ${parseInt(parts[1])}월 ${parseInt(parts[2])}일`;

  return { date: formattedDate, dayOfWeek };
};

const FeeApplyDateModal: React.FC<FeeApplyDateModalProps> = ({
  isOpen,
  applyStartDate,
  isImmediateApply,
  onClose
}) => {
  if (!isOpen) return null;

  const { date, dayOfWeek } = formatDateWithDayOfWeek(applyStartDate);

  /** 배경 클릭 시 닫기 */
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div style={styles.overlay} onClick={handleBackdropClick}>
      <style>
        {`
          @keyframes feeModalSlideIn {
            from {
              opacity: 0;
              transform: translateY(-10px) scale(0.98);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          .fee-modal-btn:hover {
            opacity: 0.9;
            transform: scale(1.02);
          }
        `}
      </style>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* 헤더 */}
        <div style={styles.header}>
          <h3 style={styles.title}>
            <span style={styles.successIcon}>
              <i className="fas fa-check"></i>
            </span>
            수수료 설정 완료
          </h3>
        </div>

        {/* 본문 */}
        <div style={styles.body}>
          <div style={styles.dateBox}>
            <div style={styles.dateLabel}>수수료 적용 시작일</div>
            <div>
              <span style={styles.dateValue}>{date}</span>
              <span style={styles.dayOfWeek}>({dayOfWeek})</span>
            </div>
          </div>

          <div style={styles.message}>
            {isImmediateApply ? (
              <>
                오늘이 수요일이므로 설정한 수수료가<br />
                <strong>즉시 적용</strong>됩니다.
              </>
            ) : (
              <>
                설정 수수료 적용일 : <strong>다음 주 수요일</strong>
              </>
            )}
          </div>

          <span style={isImmediateApply ? styles.immediateTag : styles.nextWeekTag}>
            {isImmediateApply ? '즉시 적용' : '다음 주 수요일부터 적용'}
          </span>
        </div>

        {/* 푸터 */}
        <div style={styles.footer}>
          <button
            style={styles.button}
            className="fee-modal-btn"
            onClick={onClose}
          >
            확인
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeeApplyDateModal;
